import React, { forwardRef, useImperativeHandle, useState } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ButtonBase from '@mui/material/ButtonBase';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useStripe } from '@stripe/react-stripe-js';
import { useDispatch, useSelector } from 'react-redux';

import { StripeWrapper } from '../../../../components';
import { alertActions, checkoutActions } from '../../../../store/actions';
import { getACHPaymentIntent } from '../../../../helpers/PaymentHandlers';
import { EditNoteIcon, SolidSuccessIcon } from '../../../../components/Icons';
import { ACH_AUTOMATIC, ACH_MANUAL } from '../../../../constants';
import { achIntentDetailsState } from '../../../../store/reducers/CheckouFormReducer';

const emptyBankDetails = {
	routingNumber: '',
	accountNumber: '',
	accountHolderName: '',
};

const ACHForm = forwardRef((props, ref) => {
	const stripe = useStripe();
	const dispatch = useDispatch();

	const donationDetails = useSelector((state) => state.donationCheckout);
	const achMode = donationDetails.achMode;

	const [cardDetails, setCardDetails] = useState();
	const [clientSecret, setClientSecret] = useState();
	const [loadingMode, setLoadingMode] = useState(null); // 'automatic' | 'manual' while the intent request is in flight
	const [bankDetails, setBankDetails] = useState(emptyBankDetails);

	const setField = (name, value) => dispatch(checkoutActions.updateCheckoutState({ target: { name, value } }));

	const requestIntent = async (mode) => {
		setLoadingMode(mode);
		try {
			const intent = await getACHPaymentIntent(donationDetails, mode);
			setClientSecret(intent.client_secret);
			setField('achIntentDetails', {
				donation_id: intent.donation_id ?? null,
				start_date: intent.start_date ?? null,
				end_date: intent.end_date ?? null,
				invoice_id: intent.invoice_id ?? null,
				stripe_sub_id: intent.stripe_sub_id ?? null,
			});

			if (mode === ACH_AUTOMATIC) {
				const paymentIntent = await stripe.collectBankAccountForPayment({
					clientSecret: intent.client_secret,
					params: {
						payment_method_type: 'us_bank_account',
						payment_method_data: {
							billing_details: {
								name: donationDetails.userContactDetails.firstName,
								email: donationDetails.userContactDetails.email,
							},
						},
					},
					expand: ['payment_method'],
				});

				if (paymentIntent.paymentIntent?.payment_method) {
					setCardDetails(paymentIntent.paymentIntent);
					setField('achPaymentReady', true);
				} else {
					// Automatic connection failed/cancelled — go back to the choose screen
					// rather than resetting the whole payment method (softer landing).
					handleBack();
					dispatch(alertActions.error('Something went wrong, try selecting the payment again'));
				}
			}
		} catch (error) {
			handleBack();
			dispatch(alertActions.error('Something went wrong with the payment'));
		} finally {
			setLoadingMode(null);
		}
	};

	const handleChooseMode = (mode) => {
		setField('achMode', mode);
		setField('achPaymentReady', false);
		requestIntent(mode);
	};

	// Clears all in-progress state (typed fields, connected bank, intent details) and
	// returns to the two-button choose screen — used both by the explicit Back button and
	// as the failure-recovery path when the automatic connection doesn't go through.
	const handleBack = () => {
		setCardDetails(undefined);
		setClientSecret(undefined);
		setBankDetails(emptyBankDetails);
		setField('achMode', null);
		setField('achPaymentReady', false);
		setField('achIntentDetails', achIntentDetailsState);
	};

	const handleBankDetailChange = (field) => (event) => {
		let value = event.target.value;
		if (field === 'routingNumber' || field === 'accountNumber') {
			value = value.replace(/\D/g, '');
		}
		const updated = { ...bankDetails, [field]: value };
		setBankDetails(updated);
		setField('achPaymentReady', !!(updated.routingNumber && updated.accountNumber));
	};

	useImperativeHandle(ref, () => ({
		async confirmUsBankAccountPayment() {
			try {
				if (achMode === ACH_MANUAL) {
					// Raw routing/account numbers go browser → Stripe directly here and are
					// never sent to our own backend or stored in Redux — same pattern the
					// country site uses for ACH manual entry.
					return await stripe.confirmUsBankAccountPayment(clientSecret, {
						payment_method: {
							us_bank_account: {
								routing_number: bankDetails.routingNumber,
								account_number: bankDetails.accountNumber,
								account_holder_type: 'individual',
								account_type: 'checking', // checking-only, same as country site
							},
							billing_details: {
								name:
									bankDetails.accountHolderName?.trim() ||
									`${donationDetails.userContactDetails.firstName} ${donationDetails.userContactDetails.lastName}`,
								email: donationDetails.userContactDetails.email,
							},
						},
					});
				}
				return await stripe.confirmUsBankAccountPayment(clientSecret);
			} catch (error) {
				console.error(error);
			}
		},
	}));

	if (!achMode) {
		return (
			<Box className="ACHComponent">
				<Typography className="ACHChooseLabel">We abide by strictest industry standards, and never store any of your banking information.</Typography>
				<Stack className="ACHChooseButtons">
					<ButtonBase className="ACHChoiceCard ACHChoiceCard--automatic" onClick={() => handleChooseMode(ACH_AUTOMATIC)}>
						<AccountBalanceIcon className="ACHChoiceIcon" />
						<Box className="ACHChoiceText">
							<Typography className="ACHChoiceTitle">Donate using automated payment</Typography>
						</Box>
					</ButtonBase>
					<ButtonBase className="ACHChoiceCard ACHChoiceCard--manual" onClick={() => handleChooseMode(ACH_MANUAL)}>
						<EditNoteIcon className="ACHChoiceIcon" width={32}/>
						<Box className="ACHChoiceText">
							<Typography className="ACHChoiceTitle">Use routing and account number</Typography>
						</Box>
					</ButtonBase>
				</Stack>
			</Box>
		);
	}

	return (
		<Box className="ACHComponent">
			<Stack direction="row" className="ACHHeader">
				<IconButton size="small" className="ACHBackButton" onClick={handleBack} aria-label="Back to payment options">
					<ArrowBackIcon fontSize="small" />
				</IconButton>
				<Typography variant="subtitle1" className="ACHHeaderText">
					{achMode === ACH_AUTOMATIC ? 'Donate using automated payment' : 'Use routing and account number'}
				</Typography>
			</Stack>

			{achMode === ACH_AUTOMATIC && (
				<Stack className="ACHBankDetails">
					{loadingMode === ACH_AUTOMATIC ? (
						<>
							<Skeleton height={15} width={30} />
							<Skeleton height={15} width={30} />
						</>
					) : cardDetails?.payment_method ? (
						<>
							<Typography>{cardDetails.payment_method.us_bank_account.bank_name}</Typography>
							<Stack className="AccNumClass">
								<Typography>******{cardDetails.payment_method.us_bank_account.last4}</Typography>
								<SolidSuccessIcon />
							</Stack>
						</>
					) : null}
				</Stack>
			)}

			{achMode === ACH_MANUAL && (
				<Stack className="ACHManualForm" spacing={2}>
					<TextField
						label="Routing Number"
						required
						inputMode="numeric"
						autoComplete="off"
						inputProps={{ maxLength: 9 }}
						value={bankDetails.routingNumber}
						onChange={handleBankDetailChange('routingNumber')}
						fullWidth
					/>
					<TextField
						label="Account Number"
						required
						inputMode="numeric"
						autoComplete="off"
						value={bankDetails.accountNumber}
						onChange={handleBankDetailChange('accountNumber')}
						fullWidth
					/>
					<TextField
						label="Account Holder Name"
						autoComplete="off"
						value={bankDetails.accountHolderName}
						onChange={handleBankDetailChange('accountHolderName')}
						fullWidth
					/>
				</Stack>
			)}
		</Box>
	);
});

const StripeACHForm = forwardRef((props, ref) => {
	return props.state.stripePublicKey ? (
		<StripeWrapper public_key={props.state.stripePublicKey}>
			<ACHForm ref={ref} />
		</StripeWrapper>
	) : null;
});

export default StripeACHForm;
